import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout from '../components/LegalPageLayout';

const Privacy = () => (
  <LegalPageLayout
    title="Privacy Policy"
    subtitle="How Aurora Wealth Reserves collects, uses, and protects your personal information."
  >
    <p>
      Aurora Wealth Reserves (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects your privacy.
      This Privacy Policy explains how we collect, use, share, and safeguard information when you use
      our websites, mobile experiences, online banking, and related services (collectively, the &ldquo;Services&rdquo;).
    </p>

    <h2>1. Information We Collect</h2>
    <p>We may collect the following categories of information:</p>
    <ul>
      <li>
        <strong>Identity &amp; contact data</strong> — name, address, email, phone number, date of birth,
        and government-issued identifiers when required to open or maintain an account.
      </li>
      <li>
        <strong>Account &amp; transaction data</strong> — account numbers, balances, payment history,
        transfers, deposits, and related activity.
      </li>
      <li>
        <strong>Authentication data</strong> — login credentials, multi-factor verification details,
        device and session signals used to protect your account.
      </li>
      <li>
        <strong>Technical &amp; usage data</strong> — IP address, browser type, device identifiers,
        pages visited, and approximate location derived from IP when needed for security and service improvement.
      </li>
      <li>
        <strong>Communications</strong> — messages you send to support, and records of calls or chats
        where permitted by law.
      </li>
    </ul>

    <h2>2. How We Use Information</h2>
    <p>We use personal information to:</p>
    <ul>
      <li>Provide, operate, and improve banking and wealth services</li>
      <li>Verify identity, prevent fraud, and protect accounts</li>
      <li>Process transactions and respond to service requests</li>
      <li>Meet legal, regulatory, tax, and audit obligations</li>
      <li>Send important account notices and, where permitted, service updates</li>
      <li>Analyze usage to improve reliability, accessibility, and customer experience</li>
    </ul>

    <h2>3. How We Share Information</h2>
    <p>
      We do not sell your personal information. We may share information with:
    </p>
    <ul>
      <li>Service providers who process data on our behalf under confidentiality obligations</li>
      <li>Payment networks, clearing houses, and counterparties needed to complete transactions</li>
      <li>Regulators, law enforcement, or courts when required by law or to protect rights and safety</li>
      <li>Affiliates or successors in connection with a corporate reorganization, subject to this Policy</li>
    </ul>

    <h2>4. Cookies &amp; Similar Technologies</h2>
    <p>
      We use essential cookies to keep you signed in and secure your session. We may also use analytics
      cookies to understand how the Services are used. You can control cookies through your browser settings;
      disabling essential cookies may affect online banking functionality.
    </p>

    <h2>5. Data Security &amp; Retention</h2>
    <p>
      We use administrative, technical, and physical safeguards designed to protect personal information,
      including encryption in transit, access controls, and monitoring. No method of transmission or storage
      is completely secure. We retain information as long as needed to provide Services, meet legal obligations,
      resolve disputes, and enforce agreements.
    </p>

    <h2>6. Your Choices &amp; Rights</h2>
    <p>
      Depending on your location, you may have rights to access, correct, delete, or restrict certain
      personal information, or to opt out of certain marketing communications. Account-critical notices
      cannot be opted out of while you maintain an account. To submit a request, contact{' '}
      <a href="mailto:support@aurorawealthreserves.com">support@aurorawealthreserves.com</a>.
    </p>

    <h2>7. Children&rsquo;s Privacy</h2>
    <p>
      Our Services are not directed to children under 13 (or the equivalent minimum age in your jurisdiction).
      We do not knowingly collect personal information from children without appropriate parental or guardian consent.
    </p>

    <h2>8. International Users</h2>
    <p>
      If you access the Services from outside the United States, your information may be processed in the
      United States or other countries where we or our providers operate. Those locations may have different
      data-protection laws than your home country.
    </p>

    <h2>9. Changes to This Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date at the top
      of this page reflects the latest revision. Continued use of the Services after changes become effective
      constitutes acceptance of the updated Policy where permitted by law.
    </p>

    <h2>10. Contact Us</h2>
    <p>
      Questions about privacy may be sent to{' '}
      <a href="mailto:support@aurorawealthreserves.com">support@aurorawealthreserves.com</a> or through our{' '}
      <Link to="/contact">Contact</Link> page.
    </p>
  </LegalPageLayout>
);

export default Privacy;
